import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

import logo from "../assets/images/landing-new/logo.svg";
import heroImg from "../assets/images/landing-new/hero.webp";
import warehouseImg from "../assets/images/landing-new/warehouse.webp";
import carrierImg from "../assets/images/landing-new/carrier.webp";
import phoneImg from "../assets/images/landing-new/phone.png";
import { getOperatorUrl } from "../config/site";

import "../assets/styles/landing-new.css";

const faqItems = [
  {
    q: "Что такое iTruck?",
    a: "iTruck — платформа, которая помогает грузовладельцам, водителям, транспортным компаниям и экспедиторам находить друг друга и договариваться о перевозках.",
  },
  {
    q: "Кто устанавливает стоимость перевозки?",
    a: "Стоимость, сроки и условия согласовываются непосредственно между участниками перевозки.",
  },
  {
    q: "Регистрация бесплатная?",
    a: "Да, создать аккаунт можно бесплатно. Условия использования отдельных функций уточняйте в сервисе.",
  },
  {
    q: "Можно ли работать транспортной компании?",
    a: "Да. Веб-кабинет подходит для грузовладельцев, транспортных компаний и экспедиторов, которым нужны грузы и транспорт.",
  },
  {
    q: "Как начать работу?",
    a: "Зарегистрируйтесь, заполните профиль и перейдите к размещению груза или поиску подходящей заявки.",
  },
];

const Landing = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => {
      const next = !prev;
      return next;
    });
  }, []);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeMenu]);

  useEffect(() => {
    document.title = "iTruck — грузы и транспорт на одной платформе";
  }, []);

  const loginUrl = getOperatorUrl("/login");
  const registerUrl = getOperatorUrl("/registration");

  return (
    <div className="itruck-landing">
      <header>
        <div className="il-wrap il-nav">
          <Link to="/" aria-label="iTruck — главная">
            <img className="il-logo" src={logo} alt="iTruck" />
          </Link>
          <nav className={`il-nav-links${menuOpen ? " open" : ""}`}>
            <a href="#shippers" onClick={closeMenu}>Грузовладельцам</a>
            <a href="#carriers" onClick={closeMenu}>Перевозчикам</a>
            <a href="#how" onClick={closeMenu}>Как это работает</a>
            <a href="#faq" onClick={closeMenu}>Вопросы</a>
          </nav>
          <div className="il-nav-actions">
            <a className="il-signin" href={loginUrl}>Войти</a>
            <a className="il-btn small" href={registerUrl}>Регистрация</a>
            <button
              className="il-menu"
              aria-label={menuOpen ? "Закрыть меню" : "Открыть меню"}
              aria-expanded={String(menuOpen)}
              onClick={toggleMenu}
            >
              {menuOpen ? "×" : "☰"}
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="il-hero">
          <img
            className="il-hero-image"
            src={heroImg}
            alt="Фура с надписью iTruck на цифровой карте маршрутов"
          />
          <div className="il-wrap il-hero-inner">
            <div className="il-hero-copy">
              <div className="il-eyebrow">ЦИФРОВАЯ ПЛАТФОРМА ПЕРЕВОЗОК</div>
              <h1>
                Ваш груз.<br />
                Ваш транспорт.<br />
                <span>Один маршрут.</span>
              </h1>
              <p>
                Находите друг друга, выбирайте направление
                <br className="il-desktop" /> и договаривайтесь о перевозке напрямую.
              </p>
              <div className="il-actions">
                <a className="il-btn" href={registerUrl}>Найти транспорт</a>
                <a className="il-btn outline" href={registerUrl}>Найти груз</a>
              </div>
              <div className="il-hero-note">
                Бесплатная регистрация · Для бизнеса и перевозчиков
              </div>
            </div>
          </div>
        </section>

        {/* Journey strip */}
        <div className="il-wrap il-journey">
          <div className="il-journey-label">ВЕСЬ ПУТЬ В ОДНОМ МЕСТЕ</div>
          <div>
            <span>01</span>
            <strong>Разместите груз</strong>
          </div>
          <div>
            <span>02</span>
            <strong>Найдите перевозчика</strong>
          </div>
          <div>
            <span>03</span>
            <strong>Договоритесь о рейсе</strong>
          </div>
        </div>

        {/* Audience cards */}
        <section className="il-wrap il-section" id="solutions">
          <div className="il-section-head">
            <div>
              <div className="il-eyebrow">ПО ОБЕ СТОРОНЫ МАРШРУТА</div>
              <h2>
                Для тех, кто отправляет.<br />
                И тех, кто перевозит.
              </h2>
            </div>
            <p>
              Одна платформа для поиска
              <br />
              грузов, транспорта и новых партнёров.
            </p>
          </div>
          <div className="il-audience-grid">
            <article className="il-audience" id="shippers">
              <div className="il-audience-art">
                <img src={warehouseImg} alt="Склад, паллеты и грузовой автомобиль" loading="lazy" />
              </div>
              <div className="il-audience-copy">
                <div className="il-eyebrow">ГРУЗОВЛАДЕЛЬЦАМ</div>
                <h3>
                  Груз готов.<br />
                  Найдите свой транспорт.
                </h3>
                <p>
                  Укажите маршрут и параметры груза. Свяжитесь с перевозчиком
                  и согласуйте стоимость, сроки и условия.
                </p>
                <ul>
                  <li>Заявки на перевозку в веб-кабинете</li>
                  <li>Подбор под ваш маршрут и тип груза</li>
                  <li>Прямое общение с перевозчиком</li>
                </ul>
                <a className="il-btn" href={registerUrl}>Разместить груз</a>
              </div>
            </article>
            <article className="il-audience" id="carriers">
              <div className="il-audience-art">
                <img src={carrierImg} alt="Грузовой автомобиль и смартфон с маршрутом" loading="lazy" />
              </div>
              <div className="il-audience-copy">
                <div className="il-eyebrow">ВОДИТЕЛЯМ И ПЕРЕВОЗЧИКАМ</div>
                <h3>
                  Следующий рейс<br />
                  начинается здесь.
                </h3>
                <p>
                  Выбирайте грузы по своему направлению и типу транспорта.
                  Уточняйте детали с заказчиком до выезда.
                </p>
                <ul>
                  <li>Поиск подходящих грузов</li>
                  <li>Параметры заявки под рукой</li>
                  <li>Мобильное приложение для водителя</li>
                </ul>
                <a className="il-btn outline" href={registerUrl}>Найти груз</a>
              </div>
            </article>
          </div>
        </section>

        {/* Business section */}
        <section className="il-wrap il-business" id="business">
          <div className="il-business-copy">
            <div className="il-eyebrow">ТРАНСПОРТНЫМ КОМПАНИЯМ И ЭКСПЕДИТОРАМ</div>
            <h2>
              Больше возможностей<br />
              для вашей логистики.
            </h2>
            <p>
              Работайте с грузами и транспортом в веб-кабинете. Находите
              партнёров для заявок клиентов и обсуждайте детали перевозки напрямую.
            </p>
            <a className="il-btn white" href={registerUrl}>Создать аккаунт компании</a>
          </div>
          <div className="il-business-ui">
            <div className="il-ui-top">
              <b>iTruck</b>
              <span>Веб-кабинет</span>
            </div>
            <h3>Всё для вашего рейса</h3>
            <div className="il-ui-row">
              <span className="il-ui-icon">01</span>
              <div>
                <strong>Грузы</strong>
                <p>Заявки и параметры перевозки</p>
              </div>
            </div>
            <div className="il-ui-row">
              <span className="il-ui-icon">02</span>
              <div>
                <strong>Транспорт</strong>
                <p>Машины и направления</p>
              </div>
            </div>
            <div className="il-ui-row">
              <span className="il-ui-icon">03</span>
              <div>
                <strong>Контакты</strong>
                <p>Связь с участниками перевозки</p>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="il-wrap il-section" id="how">
          <div className="il-section-head">
            <div>
              <div className="il-eyebrow">ПРОСТО НАЧАТЬ</div>
              <h2>
                От поиска до договорённости.<br />
                В три шага.
              </h2>
            </div>
            <p>
              Вы выбираете партнёра.<br />
              Условия согласовываете между собой.
            </p>
          </div>
          <div className="il-steps">
            <article>
              <div className="il-step-line">
                <span>01</span>
              </div>
              <h3>Укажите задачу</h3>
              <p>
                Создайте заявку на перевозку или найдите груз по вашему
                направлению и типу транспорта.
              </p>
            </article>
            <article>
              <div className="il-step-line">
                <span>02</span>
              </div>
              <h3>Свяжитесь напрямую</h3>
              <p>
                Обсудите маршрут, дату погрузки, стоимость и необходимые
                документы.
              </p>
            </article>
            <article>
              <div className="il-step-line">
                <span>03</span>
              </div>
              <h3>Согласуйте рейс</h3>
              <p>
                Договоритесь об условиях перевозки и переходите к работе с
                выбранным партнёром.
              </p>
            </article>
          </div>
        </section>

        {/* App section */}
        <section className="il-app-section" id="app">
          <div className="il-wrap il-app-grid">
            <div className="il-phone-stage">
              <img
                src={phoneImg}
                alt="Экран поиска грузов в приложении iTruck"
                loading="lazy"
              />
              <span>iTruck · Мобильное приложение</span>
            </div>
            <div className="il-app-copy">
              <div className="il-eyebrow">В ОФИСЕ. В ПУТИ. НА СВЯЗИ.</div>
              <h2>
                Ваш следующий груз —<br />
                в вашем телефоне.
              </h2>
              <p>
                Находите заявки там, где вам удобно.
                <br />
                Веб-кабинет для организации перевозок
                <br />
                и мобильное приложение для работы в дороге.
              </p>
              <div className="il-app-feature">
                <b>Удобный поиск</b>
                <span>Маршрут, тип груза и параметры транспорта.</span>
              </div>
              <div className="il-app-feature">
                <b>Прямой контакт</b>
                <span>Обсудите детали с другой стороной перевозки.</span>
              </div>
              <a className="il-btn" href={loginUrl}>Открыть iTruck</a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="il-wrap il-section il-faq" id="faq">
          <div>
            <div className="il-eyebrow">ОТВЕТЫ НА ВОПРОСЫ</div>
            <h2>
              Перед первым<br />
              рейсом.
            </h2>
            <p>Главное о работе платформы.</p>
          </div>
          <div className="il-faq-list">
            {faqItems.map((item, i) => (
              <details key={i} open={i === 0}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="il-wrap il-cta">
          <div>
            <div className="il-eyebrow">ВАШ СЛЕДУЮЩИЙ МАРШРУТ</div>
            <h2>
              Давайте двигаться<br />
              вместе.
            </h2>
          </div>
          <div>
            <a className="il-btn white" href={registerUrl}>
              Присоединиться к iTruck
            </a>
            <p>Начните с бесплатной регистрации</p>
          </div>
        </section>
      </main>

      <footer className="il-wrap">
        <div className="il-footer-top">
          <div>
            <Link to="/">
              <img className="il-logo" src={logo} alt="iTruck" />
            </Link>
            <p>
              Соединяем людей.<br />
              Двигаем грузы.
            </p>
          </div>
          <div>
            <b>Платформа</b>
            <a href="#shippers">Грузовладельцам</a>
            <a href="#carriers">Перевозчикам</a>
            <a href="#business">Компаниям и экспедиторам</a>
          </div>
          <div>
            <b>Начать работу</b>
            <a href="#how">Как это работает</a>
            <a href={loginUrl}>Войти в кабинет</a>
            <a href={registerUrl}>Регистрация</a>
          </div>
          <div>
            <b>Информация</b>
            <a href="#faq">Вопросы и ответы</a>
            <Link to="/privacy">Конфиденциальность</Link>
            <Link to="/terms">Условия использования</Link>
          </div>
        </div>
        <div className="il-footer-bottom">
          <span>© 2026 iTruck</span>
          <span>Платформа грузоперевозок по СНГ</span>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
